import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../AuthContext";
import "./CreateExams.css";
// No Notification import – everything is inline now

import {
  fetchExams,
  fetchExamQuestions,
  createExamSession,
  registerUser,
  fetchCareHomes,
  fetchUsers,
  fetchPresetsByExam
} from "../utils/api";

const randomName = (prefix = "field") =>
  `${prefix}_${Math.random().toString(36).substring(2, 8)}`;

// Generate unique username
async function generateUniqueUsername(firstName, surname, token) {
  const base = (firstName.slice(0, 3) + surname.slice(0, 3)).toLowerCase();
  const existingUsers = await fetchUsers(token);
  const existingUsernames = new Set(
    existingUsers.map((u) => (u.username || "").toLowerCase())
  );

  let candidate = base;
  let counter = 1;
  while (existingUsernames.has(candidate)) {
    candidate = base + counter;
    counter++;
  }
  return candidate;
}

const CreateExams = () => {
  const { user, logout } = useContext(AuthContext);
  const [careHomes, setCareHomes] = useState([]);
  const [selectedHome, setSelectedHome] = useState("");
  const [allUsers, setAllUsers] = useState([]);

  console.log(user?.role);
  console.log(user?.carehome);

  // For each row, store { firstName, surname }
  const [names, setNames] = useState(
    Array.from({ length: 9 }, () => ({ firstName: "", surname: "" }))
  );

  // For suggestions
  const [suggestions, setSuggestions] = useState(
    Array.from({ length: 9 }, () => ({
      firstNameSuggestions: [],
      surnameSuggestions: [],
      showFirstNameSuggestions: false,
      showSurnameSuggestions: false,
    }))
  );

  // Exam data
  const [exams, setExams] = useState([]);
  const [selectedExams, setSelectedExams] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState(null);

  // Error/success state
  const [errorMessages, setErrorMessages] = useState([]);
  const [showPopup, setShowPopup] = useState(false);  // triggers error display

  // If new users are created, show the modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newUsers, setNewUsers] = useState([]);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [modalTitle, setModalTitle] = useState("New Users Created");

  const token = localStorage.getItem("token");

  // For randomizing input names
  const firstNameFields = React.useMemo(
    () => Array.from({ length: 9 }, () => randomName("fname")),
    []
  );
  const surnameFields = React.useMemo(
    () => Array.from({ length: 9 }, () => randomName("sname")),
    []
  );

  // Fetch care homes
  useEffect(() => {
    const loadCareHomes = async () => {
      try {
        const data = await fetchCareHomes(token);
        setCareHomes(data);
      } catch (error) {
        console.error("Error fetching care homes:", error);
      }
    };
    loadCareHomes();
  }, [token]);

  // Fetch exams
  useEffect(() => {
    const loadExams = async () => {
      try {
        const data = await fetchExams(token);
        setExams(data);
      } catch (error) {
        console.error("Error loading exams:", error);
      }
    };
    loadExams();
  }, [token]);

  // Fetch all users
  useEffect(() => {
    const loadAllUsers = async () => {
      try {
        const data = await fetchUsers(token);
        setAllUsers(data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    loadAllUsers();
  }, [token]);

  useEffect(() => {
    if (user?.role === "manager" && user?.carehome) {
      setSelectedHome(user.carehome);
    }
  }, [user]);
  

  // Filtered users for selected home
  const usersForSelectedHome = allUsers.filter((u) => u.carehome === selectedHome);

  // Exam selection
  const handleExamSelection = async (exam) => {
    setSelectedExams([exam]);
    setSelectedCourses([]);
    setSelectedPresetId(null);

    try {
      const data = await fetchExamQuestions(exam.id, token);
      const filteredCourses = Array.isArray(data)
        ? data
            .filter((course) => course.type === "H")
            .map((course) => ({
              ...course,
              course_id: course.course_id || course.id,
            }))
        : [];
      setCourses(filteredCourses);

      const presetData = await fetchPresetsByExam(exam.id, token);
      const filteredPresets = Array.isArray(presetData)
        ? presetData.filter((p) => p.preset_name !== "SPARE")
        : [];
      setPresets(filteredPresets);
    } catch (error) {
      console.error("Error fetching exam data:", error);
      setCourses([]);
      setPresets([]);
    }
  };

  // Preset selection
  const handlePresetSelection = (preset) => {
    setSelectedCourses([]);
    setSelectedPresetId(preset.preset_id);
    setSelectedCourses((prev) => {
      const newItems = (preset.courses || []).map((c) => ({
        course_id: c.course_id,
      }));
      const merged = [...prev, ...newItems];
      const uniqueMap = new Map();
      merged.forEach((item) => {
        uniqueMap.set(item.course_id, item);
      });
      return Array.from(uniqueMap.values());
    });
  };

  // Course selection
  const handleCourseSelection = (clickedCourse) => {
    setSelectedCourses((prevSelected) => {
      const isSelected = prevSelected.some(
        (c) => c.course_id === clickedCourse.course_id
      );
      return isSelected
        ? prevSelected.filter((c) => c.course_id !== clickedCourse.course_id)
        : [...prevSelected, { course_id: clickedCourse.course_id }];
    });
  };

  // Autocomplete for first name
  const handleFirstNameChange = (index, value) => {
    setNames((prev) => {
      const updated = [...prev];
      updated[index].firstName = value;
      return updated;
    });
  
    let matches = [];
    if (value.trim().length > 0) {
      // Get already selected users
      const selectedNames = new Set(
        names
          .filter((_, idx) => idx !== index) // exclude current field
          .map((n) => (n.firstName + " " + n.surname).toLowerCase())
      );
  
      matches = usersForSelectedHome.filter((user) => {
        const fullName = (user.first_name + " " + user.surname).toLowerCase();
        return (
          user.first_name?.toLowerCase().includes(value.toLowerCase()) &&
          !selectedNames.has(fullName)
        );
      });
    }
  
    setSuggestions((prev) => {
      const updated = [...prev];
      updated[index].firstNameSuggestions = matches;
      updated[index].showFirstNameSuggestions = true;
      return updated;
    });
  };

  const handleSelectFirstNameSuggestion = (index, user) => {
    setNames((prev) => {
      const updated = [...prev];
      updated[index].firstName = user.first_name || "";
      updated[index].surname = user.surname || "";
      return updated;
    });
    setSuggestions((prev) => {
      const updated = [...prev];
      updated[index].firstNameSuggestions = [];
      updated[index].showFirstNameSuggestions = false;
      return updated;
    });
  };

  // Autocomplete for surname
  const handleSurnameChange = (index, value) => {
    setNames((prev) => {
      const updated = [...prev];
      updated[index].surname = value;
      return updated;
    });
  
    let matches = [];
    if (value.trim().length > 0) {
      // Get already selected users
      const selectedNames = new Set(
        names
          .filter((_, idx) => idx !== index)
          .map((n) => (n.firstName + " " + n.surname).toLowerCase())
      );
  
      matches = usersForSelectedHome.filter((user) => {
        const fullName = (user.first_name + " " + user.surname).toLowerCase();
        return (
          user.surname?.toLowerCase().includes(value.toLowerCase()) &&
          !selectedNames.has(fullName)
        );
      });
    }
  
    setSuggestions((prev) => {
      const updated = [...prev];
      updated[index].surnameSuggestions = matches;
      updated[index].showSurnameSuggestions = true;
      return updated;
    });
  };

  const handleSelectSurnameSuggestion = (index, user) => {
    setNames((prev) => {
      const updated = [...prev];
      updated[index].firstName = user.first_name || "";
      updated[index].surname = user.surname || "";
      return updated;
    });
    setSuggestions((prev) => {
      const updated = [...prev];
      updated[index].surnameSuggestions = [];
      updated[index].showSurnameSuggestions = false;
      return updated;
    });
  };

  // Hide suggestions on blur
  const hideSuggestions = (index) => {
    setSuggestions((prev) => {
      const updated = [...prev];
      updated[index].showFirstNameSuggestions = false;
      updated[index].showSurnameSuggestions = false;
      return updated;
    });
  };

  // Generate exam
  const handleGenerate = async () => {
    let errors = [];
    if (!selectedHome) errors.push("Please select a care home.");
    if (!selectedExams.length) errors.push("Please select at least one exam.");
    if (!selectedCourses.length) errors.push("Please select at least one course.");

    // Only keep user entries that have both first/last name
    const validUsers = names.filter(
      (user) => user.firstName.trim() && user.surname.trim()
    );
    if (!validUsers.length) {
      errors.push("Please enter at least one full name.");
    }

    if (errors.length > 0) {
      // Trigger error display
      setErrorMessages(errors);
      setShowPopup(true);
      return;
    }

    const validCourses = selectedCourses.map((course) => course.course_id).filter(Boolean);
    if (validCourses.length === 0) {
      alert("Error: No valid courses found.");
      return;
    }

    const orderedCourseIds = courses
      .filter((c) => validCourses.includes(c.course_id))
      .map((c) => c.course_id);

    const examName = selectedExams[0]?.exam_name || "Unknown Exam";
    const selectedExamId = selectedExams[0]?.id;

    try {
      // Attempt user creation if needed
      const existingUsersResponse = await fetchUsers(token);
      const newlyCreatedUsers = [];

      for (const person of validUsers) {
        const sameNameUser = existingUsersResponse.find(
          (u) =>
            u.first_name.toLowerCase() === person.firstName.toLowerCase() &&
            u.surname.toLowerCase() === person.surname.toLowerCase() &&
            u.carehome === selectedHome
        );
        if (!sameNameUser) {
          // This user doesn't exist yet
          const username = await generateUniqueUsername(person.firstName, person.surname, token);

          const newUserObj = {
            username,
            first_name: person.firstName,
            surname: person.surname,
            name: `${person.firstName} ${person.surname}`,
            password: "password123",
            role: "staff",
            carehome: selectedHome,
          };

          const sameUsernameUser = existingUsersResponse.find(
            (u) => (u.username || "").toLowerCase() === username.toLowerCase()
          );
          if (!sameUsernameUser) {
            await registerUser(newUserObj, token);
            newlyCreatedUsers.push(newUserObj);
            existingUsersResponse.push({ ...newUserObj, id: Date.now() });
          }
        }
      }

      // Now fetch updated user list
      const updatedUsersResponse = await fetchUsers(token);
      const allUsersForSession = [];
      for (const person of validUsers) {
        const match = updatedUsersResponse.find(
          (u) =>
            u.first_name.toLowerCase() === person.firstName.toLowerCase() &&
            u.surname.toLowerCase() === person.surname.toLowerCase() &&
            u.carehome === selectedHome
        );
        if (match) allUsersForSession.push(match);
      }

      if (allUsersForSession.length === 0) {
        alert("Error: No valid users found after creation.");
        return;
      }

      // Create exam sessions
      if (examName === "General Staff Training") {
        // Insert multiple sessions, one per course
        for (const cId of orderedCourseIds) {
          const data = {
            exam_id: selectedExamId,
            exam_name: examName,
            selected_courses: [cId],
            mode: "Practice",
            users: allUsersForSession.map((u) => ({
              user_id: u.id,
              employee_name: `${u.first_name} ${u.surname}`,
              care_home: u.carehome,
            })),
          };
          await createExamSession(data, token);
        }
      } else {
        // Single session with all courses
        const data = {
          exam_id: selectedExamId,
          exam_name: examName,
          selected_courses: orderedCourseIds,
          mode: "Practice",
          users: allUsersForSession.map((u) => ({
            user_id: u.id,
            employee_name: `${u.first_name} ${u.surname}`,
            care_home: u.carehome,
          })),
        };
        await createExamSession(data, token);
      }

      // Show success
      if (newlyCreatedUsers.length > 0) {
        setNewUsers(newlyCreatedUsers);
        setModalTitle("Exams generated successfully");
        setShowSuccessModal(true);
      } else {
        // Just show success notification below the button
        setNotification({
          message: "Exams generated successfully!",
          type: "success",
        });
      }
    } catch (error) {
      console.error("Error creating exam session:", error);
      setNotification({
        message: "Failed to create exam session.",
        type: "error",
      });
    }
  };

  // Animate error popup (for missing fields)
  useEffect(() => {
    if (showPopup && errorMessages.length > 0) {
      // Show the error popup
      setTimeout(() => {
        const popup = document.querySelector(".notification-popup.error");
        if (popup) popup.classList.add("show");
      }, 50);

      // Hide after 3s
      setTimeout(() => {
        const popup = document.querySelector(".notification-popup.error");
        if (popup) popup.classList.remove("show");
      }, 3000);

      // Clear error after 3.5s
      setTimeout(() => {
        setShowPopup(false);
        setErrorMessages([]);
      }, 3500);
    }
  }, [showPopup, errorMessages]);

  // Animate success/failure
  useEffect(() => {
    if (notification.message) {
      // Show
      setTimeout(() => {
        const popup = document.querySelector(".notification-popup.success, .notification-popup.error");
        if (popup) popup.classList.add("show");
      }, 50);

      // Hide after 3s
      setTimeout(() => {
        const popup = document.querySelector(".notification-popup.success, .notification-popup.error");
        if (popup) popup.classList.remove("show");
      }, 3000);

      // Clear after 3.5s
      setTimeout(() => {
        setNotification({ message: "", type: "" });
      }, 3500);
    }
  }, [notification]);

  return (
    <form autoComplete="off" className="create-exams-form">
      {/* Hidden fields to mislead autofill */}
      <input type="text" style={{ display: "none" }} autoComplete="email" />
      <input type="password" style={{ display: "none" }} autoComplete="current-password" />

      <div className="create-exams-page">
        <div className="create-exams-container">
          <h2>Create Exam</h2>

          <label className="dropdown-label" htmlFor="care-home-select">
            {user?.role === "manager" ? "Your Care Home:" : "Select Care Home:"}
          </label>

          {user?.role === "manager" ? (
            // If manager, show care home as plain text
            <div className="carehome-display">
              {user?.carehome || "No care home assigned"}
            </div>
          ) : (
            <select
              id="care-home-select"
              value={selectedHome}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedHome(val);
                setNames(
                  Array.from({ length: 9 }, () => ({ firstName: "", surname: "" }))
                );
                setSuggestions(
                  Array.from({ length: 9 }, () => ({
                    firstNameSuggestions: [],
                    surnameSuggestions: [],
                    showFirstNameSuggestions: false,
                    showSurnameSuggestions: false,
                  }))
                );
              }}
              className="dropdown"
            >
              <option value="">-- Select a care home --</option>
              {careHomes.length ? (
                careHomes.map((home) => (
                  <option key={home.id} value={home.home_name}>
                    {home.home_name}
                  </option>
                ))
              ) : (
                <option value="" disabled>
                  No care homes found
                </option>
              )}
            </select>
          )}

          <hr className="divider" />

          <h3>Enter Names</h3>
          <div className="name-grid">
            {Array.from({ length: 3 }).map((_, rowIndex) => (
              <div className="name-row" key={rowIndex}>
                {Array.from({ length: 3 }).map((_, colIndex) => {
                  const index = rowIndex * 3 + colIndex;
                  if (index >= 9) return <div key={index} className="empty-box" />;
                  return (
                    <div key={index} className="name-box">
                      <strong>Name {index + 1}</strong>
                      <div className="name-inputs">
                        {/* First name */}
                        <div className="autocomplete-container">
                          <input
                            type="text"
                            placeholder="First Name"
                            autoComplete="new-password"
                            spellCheck={false}
                            name={firstNameFields[index]}
                            value={names[index].firstName}
                            onChange={(e) =>
                              handleFirstNameChange(index, e.target.value)
                            }
                            onBlur={() =>
                              setTimeout(() => hideSuggestions(index), 150)
                            }
                            onFocus={() =>
                              handleFirstNameChange(index, names[index].firstName)
                            }
                            className="name-input"
                          />
                          {suggestions[index].showFirstNameSuggestions &&
                            suggestions[index].firstNameSuggestions.length > 0 && (
                              <div className="autocomplete-suggestions">
                                {suggestions[index].firstNameSuggestions.map((u) => (
                                  <div
                                    key={u.id}
                                    className="autocomplete-suggestion"
                                    onMouseDown={() =>
                                      handleSelectFirstNameSuggestion(index, u)
                                    }
                                  >
                                    {u.first_name} {u.surname}
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>

                        {/* Surname */}
                        <div className="autocomplete-container">
                          <input
                            type="text"
                            placeholder="Surname"
                            autoComplete="new-password"
                            spellCheck={false}
                            name={surnameFields[index]}
                            value={names[index].surname}
                            onChange={(e) => handleSurnameChange(index, e.target.value)}
                            onBlur={() =>
                              setTimeout(() => hideSuggestions(index), 150)
                            }
                            onFocus={() =>
                              handleSurnameChange(index, names[index].surname)
                            }
                            className="name-input"
                          />
                          {suggestions[index].showSurnameSuggestions &&
                            suggestions[index].surnameSuggestions.length > 0 && (
                              <div className="autocomplete-suggestions">
                                {suggestions[index].surnameSuggestions.map((u) => (
                                  <div
                                    key={u.id}
                                    className="autocomplete-suggestion"
                                    onMouseDown={() =>
                                      handleSelectSurnameSuggestion(index, u)
                                    }
                                  >
                                    {u.first_name} {u.surname}
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <hr className="divider" />

          {/* Exams / Courses / Presets */}
          <div className="list-container">
            <div className="list-box">
              <h3>Exams</h3>
              <div className="list">
                {exams.map((exam) => (
                  <div
                    key={exam.id}
                    onClick={() => handleExamSelection(exam)}
                    className={
                      "list-item " +
                      (selectedExams.some((e) => e.id === exam.id) ? "selected" : "")
                    }
                  >
                    {exam.exam_name}
                  </div>
                ))}
              </div>
            </div>
            <div className="list-box">
              <h3>Courses</h3>
              <div className="list">
                {courses.map((course, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleCourseSelection(course)}
                    className={
                      "list-item " +
                      (selectedCourses.some((c) => c.course_id === course.course_id)
                        ? "selected"
                        : "")
                    }
                  >
                    {course.question}
                  </div>
                ))}
              </div>
            </div>
            <div className="list-box">
              <h3>Presets</h3>
              <div className="list">
                {presets.map((preset) => (
                  <div
                    key={preset.preset_id}
                    onClick={() => handlePresetSelection(preset)}
                    className={
                      "list-item " +
                      (selectedPresetId === preset.preset_id ? "selected" : "")
                    }
                  >
                    {preset.preset_name}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Generate button + notifications in normal flow */}
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <button type="button" className="generate-button" onClick={handleGenerate}>
              Generate
            </button>

            {/* If there are form errors */}
            {showPopup && errorMessages.length > 0 && (
              <div className="notification-popup error">
                {errorMessages.map((msg, i) => (
                  <p key={i}>{msg}</p>
                ))}
              </div>
            )}

            {/* If there's a success or back-end error */}
            {notification.message && (
              <div className={`notification-popup ${notification.type}`}>
                {notification.message}
              </div>
            )}
          </div>

          {/* If we created new users, show modal */}
          {showSuccessModal && newUsers.length > 0 && (
            <div className="modal">
              <div className="modal-content">
                <h3>{modalTitle}</h3>
                <p>
                  Password for all users: <strong>password123</strong>
                </p>
                <p>
                  When a user logs in, they will be required to change it.
                </p>
                <table className="user-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Username</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newUsers.map((user, i) => (
                      <tr key={i}>
                        <td>
                          {user.first_name} {user.surname}
                        </td>
                        <td>{user.username}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button className="close-btn" onClick={() => setShowSuccessModal(false)}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export default CreateExams;
